'use client'

import { XIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useTheme } from 'next-themes'
import dynamic from 'next/dynamic'
import { type CSSProperties, useState } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { OrderTypeGuideKey } from '@/state/order-type-guide'

// Lazy so the lottie player stays out of the trade page's boot bundle (see dashboard-skeleton).
const DotLottieReact = dynamic(
  () => import('@lottiefiles/dotlottie-react').then((m) => m.DotLottieReact),
  { ssr: false }
)

type GuideSide = 'buy' | 'sell'

// Every guide animation shares this canvas. The box takes the card's full width and derives
// its height from the ratio, so `contain` fills it exactly without ever cropping.
const CANVAS = { w: 372, h: 284 }
const LOTTIE_ASPECT_RATIO = `${CANVAS.w} / ${CANVAS.h}`
const LOTTIE_LAYOUT = { fit: 'contain' as const, align: [0.5, 0.5] as [number, number] }

// Some animations draw in a small part of the shared canvas, leaving wide side margins.
// These are the boxes they actually paint (canvas px, union over every frame, both sides
// and themes), so they can be zoomed to the size the original guides fill. Re-measure if an
// animation is replaced.
const CONTENT_BOX: Partial<
  Record<OrderTypeGuideKey, { x: number; y: number; w: number; h: number }>
> = {
  'limit-chase': { x: 41, y: 57, w: 284, h: 156 },
  scale: { x: 48, y: 75, w: 274, h: 133 },
  rfq: { x: 92, y: 83, w: 199, h: 119 },
  'multi-leg-spreader': { x: 62, y: 46, w: 248, h: 200 },
  'trigger-order': { x: 45, y: 88, w: 272, h: 108 },
}
// What a framed animation is zoomed toward (about TWAP's width), keeping a margin top and bottom.
const TARGET = { w: 320, h: 244 }

/** Where the animation sits so its content box is centred at the target size; null = as-is. */
function frameFor(guideKey: OrderTypeGuideKey): CSSProperties | null {
  const box = CONTENT_BOX[guideKey]
  if (!box) return null
  const scale = Math.min(TARGET.w / box.w, TARGET.h / box.h)
  if (scale <= 1) return null
  const cx = box.x + box.w / 2
  const cy = box.y + box.h / 2
  // The element grows (rather than a CSS transform) so the canvas renders at the zoomed size.
  return {
    position: 'absolute',
    width: `${scale * 100}%`,
    height: `${scale * 100}%`,
    left: `${((CANVAS.w / 2 - cx * scale) / CANVAS.w) * 100}%`,
    top: `${((CANVAS.h / 2 - cy * scale) / CANVAS.h) * 100}%`,
  }
}

// The canvas backing store is sized from the on-screen box, which the trade page's
// `zoom: 0.9` shrinks, and the browser then stretches the bitmap back up — soft edges.
// Rendering at 2× the screen density leaves headroom for that stretch.
function lottieRenderConfig() {
  const dpr = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1
  return { autoResize: true, devicePixelRatio: dpr * 2 }
}

const SIDE_TONE: Record<GuideSide, { active: string; text: string }> = {
  buy: { active: 'border-chart-mint/60 bg-chart-mint/15 text-chart-mint', text: 'text-chart-mint' },
  sell: { active: 'border-chart-red/60 bg-chart-red/15 text-chart-red', text: 'text-chart-red' },
}

/**
 * First-use explainer for one order type: an animation of how it fills, per side. The
 * Buy/Sell switch only changes the illustration — it never touches the order being built.
 */
export function OrderTypeGuide({
  guideKey,
  onClose,
}: {
  guideKey: OrderTypeGuideKey
  onClose: () => void
}) {
  const t = useTranslations('orderForm')
  const { resolvedTheme } = useTheme()
  const [side, setSide] = useState<GuideSide>('buy')
  const [renderConfig] = useState(lottieRenderConfig)
  const themeDir = resolvedTheme === 'light' ? 'light' : 'dark'
  const src = `/lottie/order-form/${themeDir}/${guideKey}-${side}.json`
  const frame = frameFor(guideKey)

  return (
    <section
      aria-label={t('orderTypeGuide.title')}
      className="border-foreground/10 mx-3 my-3 shrink-0 overflow-hidden rounded-md border"
    >
      <header className="border-foreground/10 flex items-center justify-between border-b py-1 pr-1 pl-3">
        <h3 className="text-foreground text-sm font-semibold">{t('orderTypeGuide.title')}</h3>
        <Button
          variant="ghost"
          size="icon"
          type="button"
          onClick={onClose}
          aria-label={t('close')}
          className="text-foreground/60 hover:text-foreground"
        >
          <XIcon className="size-3.5" />
        </Button>
      </header>
      <div className="flex gap-1 px-3 pt-2" role="group">
        {(['buy', 'sell'] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={side === option}
            onClick={() => setSide(option)}
            className={cn(
              'h-6 cursor-pointer rounded-sm border px-2 text-xs font-semibold transition-colors',
              side === option
                ? SIDE_TONE[option].active
                : 'border-foreground/15 text-foreground/40 hover:text-foreground/70'
            )}
          >
            {t(option)}
          </button>
        ))}
      </div>
      <div className="relative w-full overflow-hidden" style={{ aspectRatio: LOTTIE_ASPECT_RATIO }}>
        {/* Framed on a div of our own: DotLottieReact drops `style` whenever it gets a className. */}
        <div className={cn(!frame && 'size-full')} style={frame ?? undefined}>
          <DotLottieReact
            key={src}
            src={src}
            loop
            autoplay
            layout={LOTTIE_LAYOUT}
            renderConfig={renderConfig}
            className="pointer-events-none size-full select-none"
          />
        </div>
      </div>
      <div className="border-foreground/10 mx-3 flex flex-col gap-0.5 border-t py-2.5">
        <span className={cn('text-sm font-semibold', SIDE_TONE[side].text)}>
          {t(`orderTypeGuide.${guideKey}.name`)}
        </span>
        <p className="text-foreground/80 text-sm">{t(`orderTypeGuide.${guideKey}.${side}`)}</p>
      </div>
    </section>
  )
}
